import type { PnpmPackageExtensionDefinition } from '@cli-types/next-adapter/init.js'

export const NEXT_ADAPTER_DEPS = [
  '@hookform/resolvers',
  '@tanstack/react-query',
  '@tanstack/react-table@^8.21.3',
  '@tanstack/react-virtual',
  'better-auth',
  'class-variance-authority',
  'clsx',
  'date-fns',
  'drizzle-orm',
  'fuse.js',
  'geist',
  'kysely@0.28.17',
  'lucide-react@1.7.0',
  'nuqs',
  'postgres',
  'react-dropzone',
  'react-hook-form',
  'shadcn',
  'sonner',
  'tailwind-merge',
  'tw-animate-css',
  'usehooks-ts',
  'zod'
]
export const NEXT_ADAPTER_DEV_DEPS = ['drizzle-kit', 'pg', 'tsx']
export const REACT_ADMIN_DEPS = [
  '@base-ui/react',
  '@codemirror/commands',
  '@codemirror/lang-javascript',
  '@codemirror/lang-json',
  '@codemirror/lang-markdown',
  '@codemirror/state',
  '@codemirror/view',
  '@dnd-kit/core',
  '@dnd-kit/sortable',
  '@dnd-kit/utilities',
  '@floating-ui/react',
  '@tiptap/core',
  '@tiptap/extension-underline',
  '@tiptap/extension-highlight',
  '@tiptap/extension-horizontal-rule',
  '@tiptap/extension-image',
  '@tiptap/extension-list',
  '@tiptap/extension-placeholder',
  '@tiptap/extension-table',
  '@tiptap/extension-table-cell',
  '@tiptap/extension-table-header',
  '@tiptap/extension-table-row',
  '@tiptap/extension-typography',
  '@tiptap/extensions',
  '@tiptap/markdown',
  '@tiptap/pm',
  '@tiptap/react',
  '@tiptap/starter-kit',
  '@uiw/codemirror-theme-github',
  '@uiw/react-codemirror',
  'cmdk',
  'embla-carousel-react',
  'input-otp',
  'katex',
  'lodash.throttle',
  'motion',
  'react-day-picker',
  'react-hotkeys-hook',
  'react-resizable-panels',
  'recharts'
]
export const REACT_ADMIN_DEV_DEPS = ['@tailwindcss/typography']
export const CORE_DEPS = Array.from(new Set([...NEXT_ADAPTER_DEPS, ...REACT_ADMIN_DEPS]))
export const DEV_DEPS = Array.from(new Set([...NEXT_ADAPTER_DEV_DEPS, ...REACT_ADMIN_DEV_DEPS]))
export const LINTER_DEV_DEPS = ['oxlint', 'oxfmt']
export const POSTGRES_RUNTIME_DEP = 'postgres'
export const DRIZZLE_KIT_POSTGRES_DRIVER_DEP = 'pg'
export const FRESH_PNPM_ALLOWED_BUILDS = [
  '@parcel/watcher',
  'esbuild',
  'msgpackr-extract',
  'msw',
  'sharp'
] as const
export const LOCAL_PACKAGE_PREFIXES = ['workspace:', 'link:', 'file:', 'portal:']
export const TIPTAP_PACKAGE_PREFIX = '@tiptap/'
export const TIPTAP_PACKAGE_VERSION_SPEC = '^3.26.0'
export const CODEMIRROR_PACKAGE_SPECS: Record<string, string> = {
  '@codemirror/commands': '^6.10.3',
  '@codemirror/lang-javascript': '^6.2.5',
  '@codemirror/lang-json': '^6.0.2',
  '@codemirror/lang-markdown': '^6.5.0',
  '@codemirror/state': '^6.6.0',
  '@codemirror/view': '^6.43.0',
  '@uiw/codemirror-theme-github': '^4.25.10',
  '@uiw/react-codemirror': '^4.25.10'
}
export const PNPM_PACKAGE_EXTENSION_DEFINITIONS: PnpmPackageExtensionDefinition[] = [
  {
    packageName: '@uiw/codemirror-theme-github',
    selector: '@uiw/codemirror-theme-github@*',
    extension: {
      dependencies: {
        '@babel/runtime': '>=7.11.0',
        '@lezer/highlight': '*'
      }
    }
  }
]
export const STATIC_CUSTOM_DEPENDENCIES: Record<string, string[]> = {
  'content-editor': [
    'admin-globals',
    'content-editor/main-toolbar-content',
    'content-editor-utils',
    'content-editor-rich-extensions',
    'content-editor-editor-view-utils',
    'markdown-utils',
    'media-field',
    'use-content-editor',
    'use-content-editor-table-add-controls',
    'use-content-editor-source-mode',
    'use-content-editor-slash-menu',
    'tiptap',
    'tiptap-utils'
  ],
  'content-editor/tiptap-ui/blockquote-button/blockquote-button': ['use-blockquote'],
  'content-editor/tiptap-ui/code-block-button/code-block-button': ['use-code-block'],
  'content-editor/tiptap-ui/color-highlight-button/color-highlight-button': ['use-color-highlight'],
  'content-editor/tiptap-ui/color-highlight-popover/color-highlight-popover': [
    'content-editor/tiptap-ui/color-highlight-popover/color-highlight-popover-button',
    'content-editor/tiptap-ui/color-highlight-popover/color-highlight-popover-content',
    'content-editor/tiptap-ui/color-highlight-popover/highlight-colors',
    'content-editor/tiptap-ui/color-highlight-popover/source-color-highlight-popover',
    'use-color-highlight'
  ],
  'content-editor/tiptap-ui/color-highlight-popover/color-highlight-popover-content': [
    'content-editor/tiptap-ui/color-highlight-popover/highlight-color-button',
    'content-editor/tiptap-ui/color-highlight-popover/highlight-colors',
    'content-editor/use-menu-navigation',
    'use-color-highlight'
  ],
  'content-editor/tiptap-ui/color-highlight-popover/highlight-color-button': [
    'content-editor/tiptap-ui/color-highlight-button/color-highlight-button',
    'content-editor/tiptap-ui/color-highlight-popover/highlight-colors'
  ],
  'content-editor/tiptap-ui/color-highlight-popover/highlight-colors': ['use-color-highlight'],
  'content-editor/tiptap-ui/color-highlight-popover/source-color-highlight-popover': [
    'content-editor/tiptap-ui/color-highlight-popover/color-highlight-popover-button',
    'content-editor/tiptap-ui/color-highlight-popover/color-highlight-popover-content',
    'content-editor/tiptap-ui/color-highlight-popover/highlight-colors'
  ],
  'content-editor/tiptap-ui/heading-dropdown-menu/heading-dropdown-menu': [
    'content-editor/tiptap-ui/heading-dropdown-menu/heading-dropdown-menu-item',
    'use-heading',
    'use-heading-dropdown-menu'
  ],
  'content-editor/tiptap-ui/heading-dropdown-menu/heading-dropdown-menu-item': ['use-heading'],
  'content-editor/tiptap-ui/link-popover/link-content': [
    'content-editor/tiptap-ui/link-popover/link-main',
    'use-link-popover'
  ],
  'content-editor/tiptap-ui/link-popover/link-control-popover': [
    'content-editor/tiptap-ui/link-popover/link-button',
    'content-editor/tiptap-ui/link-popover/link-main'
  ],
  'content-editor/tiptap-ui/link-popover/link-popover': [
    'content-editor/tiptap-ui/link-popover/link-content',
    'content-editor/tiptap-ui/link-popover/link-control-popover',
    'use-link-popover'
  ],
  'content-editor/tiptap-ui/list-dropdown-menu/list-dropdown-menu': [
    'content-editor/tiptap-ui/list-dropdown-menu/list-dropdown-menu-item',
    'use-list',
    'use-list-dropdown-menu'
  ],
  'content-editor/tiptap-ui/list-dropdown-menu/list-dropdown-menu-item': ['use-list'],
  'content-editor/tiptap-ui/mark-button/mark-button': ['use-mark'],
  'content-editor/tiptap-ui/undo-redo-button/undo-redo-button': ['use-undo-redo'],
  'content-editor/main-toolbar-content': [
    'content-editor',
    'content-editor/math-popover',
    'use-content-editor-media-insertion'
  ],
  'content-editor/math-popover': [
    'content-editor/math-bubble-menu',
    'content-editor/math-button',
    'content-editor/math-editor-controls',
    'content-editor/math-popover-button',
    'content-editor/use-tiptap-editor',
    'markdown-utils',
    'tiptap-utils'
  ],
  'content-editor/math-bubble-menu': [
    'content-editor/math-editor-controls',
    'content-editor/use-tiptap-editor',
    'markdown-utils',
    'tiptap-utils'
  ],
  'content-editor/math-popover-button': [
    'content-editor/math-button',
    'content-editor/math-editor-controls',
    'content-editor/use-tiptap-editor',
    'tiptap-utils'
  ],
  'content-editor/media-gallery-block': ['media-field'],
  'content-editor/selection-bubble-menu': ['content-editor/math-popover'],
  'content-editor/slash-command-menu': ['content-editor-slash-commands-utils', 'tiptap-utils'],
  'content-editor/source-mode': [
    'content-editor/math-popover',
    'use-content-editor-source-mode',
    'tiptap'
  ],
  'content-editor/table-bubble-menu': [
    'content-editor-table-bubble-utils',
    'content-editor/remove-table-part-icon',
    'tiptap-utils'
  ],
  'content-editor/table-button': [
    'content-editor-table-input-utils',
    'tiptap-utils',
    'use-table-button'
  ],
  'date-range-picker': ['date-utils'],
  'gallery-field': [
    'card',
    'carousel',
    'media-field',
    'media-field-empty-state',
    'media-gallery-dialog',
    'skeleton',
    'sortable-gallery-item',
    'use-media'
  ],
  'icon-picker': ['icons-column-skeleton', 'icons-data'],
  'media-field': [
    'media-field-empty-state',
    'media-gallery-dialog',
    'media-preview',
    'media-fallback-utils',
    'use-copy-to-clipboard',
    'use-media'
  ],
  'media-field-empty-state': ['button', 'card', 'cn', 'media-gallery-dialog'],
  'nested-object-list-field': ['form-list-field-utils'],
  'progressive-blur': ['use-page-boundary-blur-visibility'],
  'upload-dropzone': ['upload-utils']
}
export const TIPTAP_TEMPLATE_DEPENDENCIES = [
  'use-blockquote',
  'use-code-block',
  'use-color-highlight',
  'content-editor/use-menu-navigation',
  'content-editor/use-tiptap-editor',
  'use-content-editor-table-add-controls',
  'use-table-button',
  'content-editor-editor-view-utils',
  'use-heading',
  'use-heading-dropdown-menu',
  'use-link-popover',
  'use-list',
  'use-list-dropdown-menu',
  'use-mark',
  'use-undo-redo',
  'content-editor-node-attrs-utils',
  'content-editor-slash-commands-utils',
  'content-editor-table-add-controls-utils',
  'content-editor-table-bubble-utils',
  'content-editor-table-input-utils',
  'content-editor-task-item-utils',
  'content-editor-source-media-utils',
  'content-editor/main-toolbar-content',
  'content-editor/source-mode',
  'tiptap-utils',
  'content-editor/media-gallery-block',
  'media-field'
]
export const CODEMIRROR_PACKAGE_DEPS = [
  '@codemirror/commands',
  '@codemirror/lang-javascript',
  '@codemirror/lang-json',
  '@codemirror/lang-markdown',
  '@codemirror/state',
  '@codemirror/view',
  '@uiw/codemirror-theme-github',
  '@uiw/react-codemirror'
]
export const TIPTAP_PACKAGE_DEPS = [
  '@tiptap/core',
  '@tiptap/extension-underline',
  '@tiptap/extension-highlight',
  '@tiptap/extension-horizontal-rule',
  '@tiptap/extension-image',
  '@tiptap/extension-list',
  '@tiptap/extension-placeholder',
  '@tiptap/extension-table',
  '@tiptap/extension-table-cell',
  '@tiptap/extension-table-header',
  '@tiptap/extension-table-row',
  '@tiptap/extension-typography',
  '@tiptap/extensions',
  '@tiptap/markdown',
  '@tiptap/pm',
  '@tiptap/react',
  '@tiptap/starter-kit'
]
export const KATEX_PACKAGE_DEPS = ['katex']
export const TAILWIND_TYPOGRAPHY_PACKAGE_DEPS = ['@tailwindcss/typography']
export const COMPONENT_PACKAGE_DEPENDENCIES: Record<string, string[]> = {
  'admin-globals': [...TAILWIND_TYPOGRAPHY_PACKAGE_DEPS, ...KATEX_PACKAGE_DEPS],
  'content-editor': [
    ...TIPTAP_PACKAGE_DEPS,
    ...CODEMIRROR_PACKAGE_DEPS,
    ...TAILWIND_TYPOGRAPHY_PACKAGE_DEPS,
    ...KATEX_PACKAGE_DEPS
  ],
  'content-editor-rich-extensions': KATEX_PACKAGE_DEPS,
  'use-content-editor': [
    ...TIPTAP_PACKAGE_DEPS,
    ...TAILWIND_TYPOGRAPHY_PACKAGE_DEPS,
    ...KATEX_PACKAGE_DEPS
  ],
  'use-content-editor-media-insertion': ['react-hotkeys-hook'],
  'use-color-highlight': ['react-hotkeys-hook'],
  'dev-mode-integrate': CODEMIRROR_PACKAGE_DEPS,
  'dev-mode-code-mirror': CODEMIRROR_PACKAGE_DEPS,
  'use-content-editor-table-add-controls': ['@tiptap/pm', '@tiptap/react'],
  'use-table-button': ['@tiptap/core'],
  'use-content-editor-source-mode': CODEMIRROR_PACKAGE_DEPS,
  'content-editor/use-menu-navigation': ['@tiptap/react'],
  'content-editor/use-tiptap-editor': ['@tiptap/react'],
  tiptap: TIPTAP_PACKAGE_DEPS,
  'upload-dropzone': ['react-dropzone']
}
export const MARKDOWN_IT_RENDERER_SPEC = 'markdown-it@^13.0.2'
export const MARKDOWN_RENDERER_DEPS = [
  'katex',
  MARKDOWN_IT_RENDERER_SPEC,
  'markdown-it-dollarmath',
  'sanitize-html'
]
export const MARKDOWN_RENDERER_DEV_DEPS = ['@types/markdown-it@13', '@types/sanitize-html']
